import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendToTerminal } from '../lib/terminalLogger';

/**
 * Hook: Escuta eventos externos e dispara navegação via Router
 *
 * Eventos:
 * - navigate:to-pedido (WebSocket order notifications)
 * - navigate:to-tool (C# WebView2)
 * - navigate:to-url (C# WebView2)
 * - role:changed (PermissionContext)
 *
 * IMPORTANTE: Este hook centraliza toda navegação disparada por eventos externos
 */
export function useExternalNavigation(
  setSelectedOrder?: (order: any) => void,
  setShowOrderDetails?: (show: boolean) => void
) {
  const navigate = useNavigate();

  useEffect(() => {
    // Event: navigate:to-pedido (from WebSocket)
    const handleNavigateToPedido = (event: Event) => {
      const customEvent = event as CustomEvent<{ pedidoId: number; itemId?: number }>;
      const { pedidoId, itemId } = customEvent.detail || {};

      sendToTerminal('🔌 [External Event] navigate:to-pedido', { pedidoId, itemId });

      // Navigate to pedidos page
      navigate('/pedidos', {
        replace: false
      });

      // Configurar pedido selecionado para abrir OrderDetailsWindow
      if (itemId && setSelectedOrder && setShowOrderDetails) {
        setSelectedOrder({
          pedidoId: String(pedidoId),
          itemId: String(itemId),
          empresa: '',
        });
        setShowOrderDetails(true);
      }
    };

    // Event: navigate:to-tool (from C# WebView2)
    const handleNavigateToTool = (event: Event) => {
      const customEvent = event as CustomEvent<{ toolId: string }>;
      const { toolId } = customEvent.detail || {};

      sendToTerminal('🔌 [External Event] navigate:to-tool', { toolId });

      // Tools são gerenciados internamente pelo ToolsMenu, não precisam de router navigation
      // Este evento já é tratado por useBootstrapEvents.ts
    };

    // Event: navigate:to-url (from C# WebView2)
    const handleNavigateToUrl = (event: Event) => {
      const customEvent = event as CustomEvent<{ url: string }>;
      const { url } = customEvent.detail || {};

      sendToTerminal('🔌 [External Event] navigate:to-url', { url });

      // External URLs abrem diretamente
      if (url) {
        window.location.href = url;
      }
    };

    // Event: role:changed (from PermissionContext)
    const handleRoleChanged = (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const newRole = customEvent.detail;

      sendToTerminal('🔌 [External Event] role:changed', { newRole });

      // Navigate to main when role changes
      navigate('/main', { replace: true });
    };

    // Register listeners
    window.addEventListener('navigate:to-pedido', handleNavigateToPedido as EventListener);
    window.addEventListener('navigate:to-tool', handleNavigateToTool as EventListener);
    window.addEventListener('navigate:to-url', handleNavigateToUrl as EventListener);
    window.addEventListener('role:changed', handleRoleChanged as EventListener);

    return () => {
      window.removeEventListener('navigate:to-pedido', handleNavigateToPedido as EventListener);
      window.removeEventListener('navigate:to-tool', handleNavigateToTool as EventListener);
      window.removeEventListener('navigate:to-url', handleNavigateToUrl as EventListener);
      window.removeEventListener('role:changed', handleRoleChanged as EventListener);
    };
  }, [navigate, setSelectedOrder, setShowOrderDetails]);
}
